import React from 'react';
import PropTypes from 'prop-types';
import ClassNames from 'classnames';
import dollar from 'affiliateIcons/dollar.svg';
import { I18nText, Amount, EmphasisTag } from '@welldev-group/react-comp-lib';
import Icon from '@welldev-group/rcl-icon';
import Tooltip from '@welldev-group/rcl-tooltip';
import DateTime from '../DateTime';
import TransactionsTableRowShowMore from './TransactionsTableRowShowMore';
import TransactionTableItemTitle from './TransactionTableItemTitle';

const TransactionTable = ({ transactions, isOverview, showAll }) => {
  if (transactions.length === 0) {
    return (
      <div className="billing__transactions-placeholder">
        <div className="billing__transactions-icon">
          <img src={dollar} alt="credit-card" />
        </div>
        <div className="billing__transactions-empty-text">
          <I18nText id="billing.transactions.list.no_transactions" />
        </div>
      </div>
    );
  }

  const transactionTypes = {
    WireTransferPayment: 'wireTransfer',
    StripePayment: 'creditCard',
    WelltravelBookingTransaction: 'walletBalance',
    SubscriptionCharge: 'walletBalance',
  };

  const transactionTooltip = {
    WireTransferPayment: 'wire_transfer',
    StripePayment: 'stripe_payment',
    WelltravelBookingTransaction: 'wallet',
    SubscriptionCharge: 'wallet',
  };

  const transactionTable = () =>
    transactions.map(({ transaction, balance }) => {
      const { id, reference, lineItemTitle, amount } = transaction;
      const orderDetailsShowUrl = reference.workspace?.orderDetailsShowUrl;
      const transactionAmountCondition = amount >= 0;

      return (
        <div className="billing-transaction-list__row" key={id}>
          <div className="grid">
            <div className="col-lg-1 col-md-11 col-9 order-1 billing-transaction-list__row-item">
              <DateTime dateTime={transaction.createdAt} format="short" />
              <div className="billing-transaction-list__row-item-header">
                <I18nText id="billing.transactions.list.table_header.date" />
              </div>
            </div>
            <div className="col-lg-3 col-md-4 col-4 order-3 billing-transaction-list__row-item billing-transaction-list__row-item-title">
              <TransactionTableItemTitle
                lineItemTitle={lineItemTitle}
                orderDetailsShowUrl={orderDetailsShowUrl}
              />
              <EmphasisTag
                type="null"
                size="small"
                text={I18n.t(
                  `billing.welltravel_boooking.transactions.emphasis.${transaction.reason}`,
                )}
              />
              <div className="billing-transaction-list__row-item-header">
                <I18nText id="billing.transactions.list.table_header.title" />
              </div>
            </div>
            <div className="col-lg-1 col-md-4 col-sm-4 col-6 order-3 billing-transaction-list__row-item billing-transaction-list__row-item-icon">
              <Tooltip
                content={I18n.t(
                  `billing.welltravel_boooking.transactions.emphasis.tooltip.${
                    transactionTooltip[transaction.transactionType]
                  }`,
                )}
                type="inverse"
                position="bottom"
                size="tiny"
              >
                <Icon
                  name={transactionTypes[transaction.transactionType]}
                  showBGColor={true}
                  color="tertiary"
                  size="normal"
                />
              </Tooltip>
              <div className="billing-transaction-list__row-item-header">
                <I18nText id="billing.transactions.list.table_header.method" />
              </div>
            </div>
            <div
              className={`col-lg-2 col-md-4 col-sm-4 col-6 order-3 billing-transaction-list__row-item billing-transaction-list__row-item-amount billing-transaction-list__row-item${
                transactionAmountCondition ? '--positive' : '--negative'
              }`}
            >
              <span>
                {`${transactionAmountCondition ? '+' : '-'} `}
                <Amount
                  locale="de-CH"
                  value={Math.abs(amount)}
                  currency={transaction.currency}
                  minFractionDigit={2}
                />
              </span>
              <div className="billing-transaction-list__row-item-header">
                <I18nText id="billing.transactions.list.table_header.amount" />
              </div>
            </div>

            <div className="col-lg-2 col-md-4 col-sm-4 col-12 order-3 billing-transaction-list__row-item">
              <Amount
                locale="de-CH"
                value={Math.abs(balance.after)}
                currency={balance.currency}
                minFractionDigit={2}
              />
              <div className="billing-transaction-list__row-item-header">
                <I18nText id="billing.transactions.list.table_header.balance" />
              </div>
            </div>
            <div className="col-lg-2 col-md-4 col-sm-4 col-12 order-3 billing-transaction-list__row-item">
              <Amount
                locale="de-CH"
                value={Math.abs(balance.reservedBalanceTill)}
                currency={balance.currency}
                minFractionDigit={2}
              />
              <div className="billing-transaction-list__row-item-header">
                <I18nText id="billing.transactions.list.table_header.reserved" />
              </div>
            </div>
            <div className="col-lg-1 col-md-1 col-3 order-lg-4 order-2 billing-transaction-list__row-item">
              <TransactionsTableRowShowMore transaction={transaction} />
            </div>
          </div>
        </div>
      );
    });

  return (
    <div
      className={ClassNames('billing-transaction-list__table col-12', {
        'col-12 billing-transaction-list__table--overview': isOverview,
      })}
    >
      <div className="billing-transaction-list__row billing-transaction-list__row--header">
        <div className="grid">
          <div className="col-lg-1 col-4 order-1 billing-transaction-list__row-item">
            <I18nText id="billing.transactions.list.table_header.date" />
          </div>
          <div className="col-lg-3 col-11 order-3 billing-transaction-list__row-item">
            <I18nText id="billing.transactions.list.table_header.title" />
          </div>
          <div className="col-lg-1 col-4 order-3 billing-transaction-list__row-item">
            <I18nText id="billing.transactions.list.table_header.method" />
          </div>
          <div className="col-lg-2 col-4 order-3 billing-transaction-list__row-item">
            <I18nText id="billing.transactions.list.table_header.amount" />
          </div>
          <div className="col-lg-2 col-4 order-3 billing-transaction-list__row-item">
            <I18nText id="billing.transactions.list.table_header.balance" />
          </div>
          <div className="col-lg-2 col-4 order-3 billing-transaction-list__row-item">
            <I18nText id="billing.transactions.list.table_header.reserved" />
          </div>
          <div className="col-1 order-lg-4 order-2 billing-transaction-list__row-item">
            <I18nText id="billing.transactions.list.table_header.detail" />
          </div>
        </div>
      </div>
      {showAll ? transactionTable() : transactionTable().slice(0, 5)}
    </div>
  );
};

TransactionTable.defaultProps = {
  isOverview: false,
  showAll: true,
};

TransactionTable.propTypes = {
  transactions: PropTypes.arrayOf(
    PropTypes.shape({
      transaction: PropTypes.shape({
        id: PropTypes.number.isRequired,
        title: PropTypes.string.isRequired,
        amount: PropTypes.string.isRequired,
        currency: PropTypes.string.isRequired,
        createdAt: PropTypes.string.isRequired,
        lineItemTitle: PropTypes.string.isRequired,
        showDetailsUrl: PropTypes.string.isRequired,
        reference: PropTypes.shape({
          workspace: PropTypes.shape({
            orderDetailsShowUrl: PropTypes.string,
          }),
        }).isRequired,
      }),
      balance: PropTypes.shape({
        before: PropTypes.string.isRequired,
        after: PropTypes.string.isRequired,
        currency: PropTypes.string.isRequired,
      }),
    }),
  ).isRequired,
  isOverview: PropTypes.bool,
  showAll: PropTypes.bool,
};

export default TransactionTable;
